import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store.js';
import Layout from './components/Layout.jsx';
import { Spinner } from './components/ui.jsx';

import LoginPage from './pages/auth/LoginPage.jsx';
import SignupPage from './pages/auth/SignupPage.jsx';
import MagicLinkPage from './pages/auth/MagicLinkPage.jsx';
import AcceptInvitationPage from './pages/auth/AcceptInvitationPage.jsx';

import IndividualDashboard from './pages/IndividualDashboard.jsx';
import AssessmentsPage from './pages/AssessmentsPage.jsx';
import TakeAssessmentPage from './pages/TakeAssessmentPage.jsx';
import ResultsPage from './pages/ResultsPage.jsx';

import CompanyInsightsPage from './pages/insights/CompanyInsightsPage.jsx';
import GroupsListPage from './pages/insights/GroupsListPage.jsx';
import GroupInsightsPage from './pages/insights/GroupInsightsPage.jsx';
import IndividualsPage from './pages/insights/IndividualsPage.jsx';

import OrganizationSetup from './pages/setup/OrganizationSetup.jsx';
import UsersSetup from './pages/setup/UsersSetup.jsx';
import PermissionsSetup from './pages/setup/PermissionsSetup.jsx';
import BillingPage from './pages/BillingPage.jsx';
import AuditLogPage from './pages/AuditLogPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuthStore();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

export default function App() {
  const loadMe = useAuthStore(s => s.loadMe);
  useEffect(() => { loadMe(); }, [loadMe]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/magic" element={<MagicLinkPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/reset-password" element={<MagicLinkPage purpose="password_reset" />} />

      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<IndividualDashboard />} />
        <Route path="/assessments" element={<AssessmentsPage />} />
        <Route path="/assessments/:id/take" element={<TakeAssessmentPage />} />
        <Route path="/assessments/:id/results" element={<ResultsPage />} />
        <Route path="/results/:id" element={<ResultsPage />} />

        <Route path="/insights/company" element={<CompanyInsightsPage />} />
        <Route path="/insights/groups" element={<GroupsListPage />} />
        <Route path="/insights/groups/:id" element={<GroupInsightsPage />} />
        <Route path="/insights/individuals" element={<IndividualsPage />} />
        <Route path="/insights/individuals/:userId" element={<IndividualDashboard fromAdmin />} />

        <Route path="/setup/organization" element={<OrganizationSetup />} />
        <Route path="/setup/users" element={<UsersSetup />} />
        <Route path="/setup/permissions" element={<PermissionsSetup />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/audit-log" element={<AuditLogPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
